import { IOrganization, OrgUtils } from "@smartrr/shared/entities/Organization";
import { ISchedule } from "@smartrr/shared/entities/Schedule";
import { RRuleManager } from "@smartrr/shared/entities/Schedule/RRuleManager";
import { ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useCallback } from "react";

import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { useOrganization } from "../../../utils";

export function getFrequencyString(schedule: ISchedule, organization: IOrganization): string {
  if (schedule && organization) {
    const rruleManager = RRuleManager.fromJson(schedule, OrgUtils.getBillingSchedule(organization));

    return `${rruleManager.getScheduleConfig().deliveryFrequencyValue} ${
      rruleManager.getScheduleConfig().frequencyUnit
    }`;
  }
  return "";
}

export const useFrequencyString = (schedule: ISchedule) => {
  const organization = useOrganization();
  if (schedule && organization) {
    const rruleManager = RRuleManager.fromJson(schedule, OrgUtils.getBillingSchedule(organization));

    return `${rruleManager.getScheduleConfig().deliveryFrequencyValue} ${
      rruleManager.getScheduleConfig().frequencyUnit
    }`;
  }
  return "";
};

export function getFormattedCustomerSupportEmail(
  organization: IOrganization,
  customerShopifyId: number | string
): string {
  const subjectLine = typedLocalize("errorMessages.contactSupportSubject", {
    customerShopifyId,
  });
  const email = organization.shopifyStoreData?.customer_email;
  return `mailto:${email}?subject=${subjectLine}`;
}

export function isVariantMissingFromSellingPlan(
  id: string | undefined,
  sellingPlanId: string,
  groups: ISmartrrSellingPlanGroup[],
  variantPlanMapping: {
    [variantId: string]: ISmartrrSellingPlanGroup[];
  }
) {
  const variantId = id ?? "";
  if (sellingPlanId === "" || variantId === "") {
    return false;
  }
  const group = groups.find(group => {
    return (
      group.sellingPlans.find(plan => {
        return plan.shopifyId === sellingPlanId;
      }) !== undefined
    );
  });
  if (group === undefined) {
    return false;
  }
  if (variantPlanMapping[variantId] === undefined) {
    return true;
  }
  const checkedGroup = variantPlanMapping[variantId].find(variantGroup => {
    return variantGroup.shopifyNumericId === shopifyGidToNumber(group?.shopifyId!);
  });
  return checkedGroup === undefined;
}

export const useMissingVariantChecker = () => {
  const sellingPlanGroups = useSmartrrCustomerSelector(state => state.sellingPlanGroups.sellingPlanGroupList);

  const sellingPlanGroupVariantMapping = useSmartrrCustomerSelector(
    state => state.sellingPlanGroups.sellingPlanGroupsByVariantId
  );

  return useCallback(
    (id: string | undefined, sellingPlanId: string) => {
      return isVariantMissingFromSellingPlan(
        id,
        sellingPlanId,
        sellingPlanGroups,
        sellingPlanGroupVariantMapping
      );
    },
    [sellingPlanGroups, sellingPlanGroupVariantMapping]
  );
};

export function checkRetentionReasonUiIdx(localizeKey, reason, retentionReasons) {
  const localizeShorthand = "subscriptions.yourSubscriptions.manageSubscriptionModal";
  const notArchivedReasons = retentionReasons.filter(
    retentionReason => !retentionReason.isArchived && !!retentionReason.text
  );
  const orderedByIdx = notArchivedReasons.sort((a, b) => a.uiIdx - b.uiIdx);
  const currentIndex = orderedByIdx.findIndex(searchedReason => searchedReason.uiIdx === reason.uiIdx);

  if (
    localize(`${localizeShorthand}.${localizeKey}${currentIndex}`) === `Pause Reason ${currentIndex + 1}` ||
    localize(`${localizeShorthand}.${localizeKey}${currentIndex}`) === `Cancel Reason ${currentIndex + 1}`
  ) {
    return reason.text?.["en-US"] ?? reason.text;
  }

  return localize(`${localizeShorthand}.${localizeKey}${currentIndex}`);
}
